/**
 * useMediaStream.ts — 카메라/마이크 관리 훅
 * GenomicAI/shared/hooks/useMediaStream.ts 에서 복사 (LandTrust용)
 */

import { useCallback, useRef, useState } from 'react';

export interface UseMediaStreamReturn {
  localStream: MediaStream | null;
  videoEnabled: boolean;
  audioEnabled: boolean;
  facingMode: 'user' | 'environment';
  start: (audioOnly?: boolean) => Promise<MediaStream>;
  toggleVideo: () => void;
  toggleAudio: () => void;
  switchCamera: () => Promise<MediaStreamTrack | null>;
  stop: () => void;
}

export function useMediaStream(): UseMediaStreamReturn {
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [videoEnabled, setVideoEnabled] = useState(true);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('user');

  const localStreamRef = useRef<MediaStream | null>(null);

  const start = useCallback(async (audioOnly = false): Promise<MediaStream> => {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: audioOnly ? false : { facingMode: 'environment' }, // 현장 촬영용 후면 카메라 기본
      audio: true,
    });
    localStreamRef.current = stream;
    setLocalStream(stream);
    return stream;
  }, []);

  const toggleVideo = useCallback(() => {
    const track = localStreamRef.current?.getVideoTracks()[0];
    if (track) { track.enabled = !track.enabled; setVideoEnabled(track.enabled); }
  }, []);

  const toggleAudio = useCallback(() => {
    const track = localStreamRef.current?.getAudioTracks()[0];
    if (track) { track.enabled = !track.enabled; setAudioEnabled(track.enabled); }
  }, []);

  const switchCamera = useCallback(async (): Promise<MediaStreamTrack | null> => {
    const stream = localStreamRef.current;
    if (!stream) return null;
    const newFacing = facingMode === 'user' ? 'environment' : 'user';
    try {
      const newStream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: newFacing }, audio: false });
      const newTrack = newStream.getVideoTracks()[0];
      const oldTrack = stream.getVideoTracks()[0];
      if (oldTrack) { oldTrack.stop(); stream.removeTrack(oldTrack); }
      stream.addTrack(newTrack);
      setLocalStream(new MediaStream(stream.getTracks()));
      setFacingMode(newFacing);
      return newTrack;
    } catch { return null; }
  }, [facingMode]);

  const stop = useCallback(() => {
    localStreamRef.current?.getTracks().forEach(t => t.stop());
    localStreamRef.current = null;
    setLocalStream(null);
  }, []);

  return { localStream, videoEnabled, audioEnabled, facingMode, start, toggleVideo, toggleAudio, switchCamera, stop };
}
